import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch } from "@mui/material";
import { faMap, faTrash, faUpload, faSpinner } from '@fortawesome/fontawesome-free-solid';
import { iReducersState } from '../../stores/reducers';
import { KmlActions } from '../../stores/reducers/kml-reducers';
import { deleteKml, toggleKmlVisibility, uploadKml, watchKmlFiles } from '../../shared/db/kml-db';
import { Button, ButtonSize, ButtonStyles } from '../Button';
import { Fa } from '../elements/fa';
import styles from './styles.module.scss';

export const KmlModal = () => {
    const dispatch = useDispatch();

    const user = useSelector((s: iReducersState) => s.auth.user);
    const { files, isUploading } = useSelector((s: iReducersState) => s.kml);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.kml')) {
            alert("Only .kml files are allowed");
            return;
        }

        dispatch(KmlActions.SET_UPLOADING(true));

        try {
            await uploadKml(user)(file);
        } catch (error) {
            console.error("Upload failed", error);
            alert("Failed to upload KML file");
        } finally {
            dispatch(KmlActions.SET_UPLOADING(false));
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleToggle = (id: string, currentStatus: boolean) => {
        toggleKmlVisibility(user)(id, !currentStatus);
    };

    const handleDelete = (id: string) => {
        if (window.confirm("Are you sure you want to delete this KML layer?")) {
            deleteKml(user)(id);
        }
    };

    const filesList = Object.values(files || {}).sort((a, b) => b.createdAt - a.createdAt);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h4>KML Layers</h4>

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".kml"
                    onChange={handleFileChange}
                />

                <Button
                    styleType={ButtonStyles.ORANGE_WHITE}
                    size={ButtonSize.SM}
                    onClick={handleUploadClick}
                    disabled={isUploading}
                >
                    {isUploading ? (
                        <><Fa icon={faSpinner} style={{ marginRight: 5 }} /> Uploading</>
                    ) : (
                        <><Fa icon={faUpload} style={{ marginRight: 5 }} /> Add KML</>
                    )}
                </Button>
            </div>

            <div className={styles.list}>
                {filesList.length > 0 ? (
                    filesList.map(file => (
                        <div key={file.id} className={styles.item}>
                            <div className={styles.name} title={file.name}>
                                <Fa icon={faMap} />
                                {file.name}
                            </div>

                            <div className={styles.actions}>
                                <Switch
                                    checked={file.isVisible}
                                    onChange={() => handleToggle(file.id, file.isVisible)}
                                    color="primary"
                                    size="small"
                                />
                                <div
                                    className={styles.deleteBtn}
                                    onClick={() => handleDelete(file.id)}
                                    title="Delete layer"
                                >
                                    <Fa icon={faTrash} />
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={styles.empty}>
                        No KML files uploaded yet.
                    </div>
                )}
            </div>
        </div>
    );
};